import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, CardHeader, Typography } from '@material-ui/core';
import { StructuredMetadataTable } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ReactNode } from 'react';

export interface EntityAboutCardProps {
  /** Fields to display as key-value pairs */
  fields?: Record<string, string | ReactNode | undefined>;
  /** Custom title */
  title?: string;
}

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  cardContent: {
    flex: '1 1 auto',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  tagsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  tagsHeader: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  tagsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    '& > *': {
      margin: 0,
    },
  },
  technologiesContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  technologiesHeader: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  technologiesWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    '& > *': {
      margin: 0,
    },
  },
  tag: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  technologyChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  emptyTags: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.875rem',
  },
  emptyTechnologies: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.875rem',
  },
}));

export function EntityAboutCard({ fields = {}, title }: EntityAboutCardProps) {
  const classes = useStyles();
  const { entity } = useEntity();

  // Filter out empty values
  const displayFields = Object.fromEntries(
    Object.entries(fields).filter(([_, value]) => value),
  );

  return (
    <Card className={classes.card}>
      <CardHeader
        title={title || entity.metadata.title || entity.metadata.name}
      />
      <CardContent className={classes.cardContent}>
        {entity.metadata.description && (
          <Typography className={classes.description}>
            {entity.metadata.description}
          </Typography>
        )}

        {Object.keys(displayFields).length > 0 && (
          <StructuredMetadataTable metadata={displayFields} />
        )}
      </CardContent>
    </Card>
  );
}
